<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import BurstingParameterTable from '@/components/bursting-parameter/BurstingParameterTable.vue'
import BurstingParameterForm from '@/components/bursting-parameter/BurstingParameterForm.vue'
import { REVISION_KEY } from '@/components/constant'
import ActionBar from '@/components/common/ActionBar.vue'
import RevisionSelect from '@/components/revision/RevisionSelect.vue'
import DownloadDialog from '@/components/common/DownloadDialog.vue'
import ImportFileStepper from '@/components/common/ImportFileStepper.vue'

const STORE_NAMESPACE = 'burstingParameter'

const store = useStore()
const route = useRoute()

const importDialog = ref(false)
const downloadDialog = ref(false)
const exportDialog = ref(false)

const revisionId = computed(() => {
  return parseInt(route.params.revisionId)
})

const items = computed(() => store.state.burstingParameter.items)
const count = computed(() => store.state.burstingParameter.count)
const importItems = computed(() => store.state.burstingParameter.importItems)
const importItemCount = computed(() => store.state.burstingParameter.importItemCount)
const downloadLoading = computed(() => store.state.burstingParameter.downloadLoading)
const exportLoading = computed(() => store.state.burstingParameter.exportLoading)

const selectedProjectId = computed(() => store.getters['project/selectedProjectId'])
const getSelectedRevision = computed(() => store.getters['project/selectedRevision'])

const revisionKey = REVISION_KEY.BURSTING_PARAMETER
const selectedRevision = computed(() => getSelectedRevision.value(revisionKey))

const table = ref(null)
const form = ref(null)

const add = () => {
  table.value.add()
}

const save = () => {
  table.value.saveChanges()
  form.value.saveChanges()
}

const resetItemState = () => store.dispatch('burstingParameter/resetItemState')

const downloadTemplate = async () => store.dispatch('burstingParameter/downloadTemplate')
const exportData = async () => store.dispatch('burstingParameter/exportData')
const fetchPaginated = async (id) => {
  await store.dispatch('burstingParameter/fetchPaginated', id)
}
const fetchAvailableReferencePointById = async (id) => {
  await store.dispatch('burstingParameter/fetchAvailableReferencePointById', id)
}
const resetState = async () => {
  await store.dispatch('burstingParameter/resetState')
}
const setCurrentRevision = (revision) => {
  store.dispatch('burstingParameterRevision/setCurrentItem', revision)
}
const fetchOptions = async () => {
  await store.dispatch('burstingParameter/fetchOptions')
}

watch(
  () => selectedRevision.value?.id,
  async (value) => {
    if (value) {
      setCurrentRevision(selectedRevision.value)
    }
  }
)

watch(revisionId, async () => {
  await fetchRelatedItems()
})

const downloadTemplateExcel = async () => {
  await downloadTemplate()
  downloadDialog.value = false
}

const exportToExcel = async () => {
  await exportData()
  exportDialog.value = false
}

const fetchRelatedItems = async () => {
  await resetState()
  await fetchOptions()
  if (revisionId.value) {
    await fetchAvailableReferencePointById(revisionId.value)
    await fetchPaginated(revisionId.value)
  }
}

onMounted(async () => {
  setCurrentRevision(selectedRevision.value)
  await fetchRelatedItems()
})
</script>

<template>
  <div>
    <ActionBar
      class="pr-3 align-center"
      download-text="template"
      export-text="data export"
      import-text="data import"
      show-add
      show-save
      show-import-right
      show-download-right
      show-export-right
      @add="add()"
      @save="save()"
      @download="downloadDialog = true"
      @export="exportDialog = true"
      @import="importDialog = true"
    >
      <template v-slot:left>
        <div class="d-flex align-center">
          <v-card-title> Bursting parameter </v-card-title>
          <RevisionSelect
            label="Revision"
            style="width: 250px"
            class="float-right"
            density="compact"
            store-namespace="burstingParameterRevision"
            :parameter="revisionKey"
          ></RevisionSelect>
        </div>
        <v-card-subtitle> Please define the bursting parameter </v-card-subtitle>
      </template>
    </ActionBar>
    <BurstingParameterForm
      v-if="selectedProjectId && selectedRevision?.id"
      ref="form"
      class="pt-3 px-4"
    />
    <BurstingParameterTable
      class="pt-3 px-4"
      ref="table"
      :items="items"
      :count="count"
      @change-page="fetchPaginated(revisionId)"
    ></BurstingParameterTable>

    <v-dialog v-model="importDialog" max-width="85%">
      <ImportFileStepper
        @close-dialog="importDialog = false"
        :storeNamespace="STORE_NAMESPACE"
        text="bursting parameter data"
        @reset-item-state="resetItemState"
      >
        <template v-slot>
          <v-alert variant="tonal" type="warning">
            The old data will be replaced by the new data
          </v-alert>
          <BurstingParameterTable :items="importItems" :count="importItemCount" :editable="false" />
        </template>
      </ImportFileStepper>
    </v-dialog>
    <DownloadDialog
      @update:dialog="downloadDialog = $event"
      :dialog="downloadDialog"
      width="500"
      :loading="downloadLoading"
      text="Download template"
      content="Do you want to download the template?"
      @download="downloadTemplateExcel"
    ></DownloadDialog>
    <DownloadDialog
      @update:dialog="exportDialog = $event"
      :dialog="exportDialog"
      width="500"
      :loading="exportLoading"
      text="Export data"
      content="Do you want to export the data?"
      @download="exportToExcel"
    ></DownloadDialog>
  </div>
</template>
